import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  FILTER_GENDER,
  FILTER_LATITUDE,
  FILTER_LOCATION_NAME,
  FILTER_LONGITUDE,
  FILTER_POSTAL_CODE,
  FILTER_PRICE
} from 'dpl/constants/query_params';
import { FEATURES } from 'dpl/shared/constants/features';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { removeEmptyValues } from 'dpl/shared/utils/object';
import isFeatureEnabled from 'dpl/util/isFeatureEnabled';

import { SimilarBreedResultsResource } from '../constants/resource_types';
import { similarBreedResultsResourceDefinition } from '../utils/queryDefinitions';
import {
  SIMILAR_BREEDS_RESULTS_TYPES,
  SIMILAR_BREEDS_VARIANTS
} from '../utils/similarBreeds';

interface UseSimilarBreedResultsArgs {
  /** Mobile web, first page. Both search views mount this. */
  enabled: boolean;
}

export default function useSimilarBreedResults({
  enabled
}: UseSimilarBreedResultsArgs) {
  const {
    breedSlug,
    citySlug,
    preappliedFilters: { stateSlug, cityName } = {}
  } = useSelector(({ server }: any) => server);
  const params = useSelector(({ queryParams }: any) => queryParams);

  const isSimilarBreedsModuleEnabled = isFeatureEnabled(
    FEATURES.REL_SIMILAR_BREEDS_MODULE
  );

  // Held as one object rather than inlined so the "See all" overlay can query
  // with the very same filters. Anything less and the overlay could disagree
  // with the module it was opened from about what "nearby" means.
  const filters = useMemo(
    () =>
      removeEmptyValues({
        postal_code: params[FILTER_POSTAL_CODE],
        lat: params[FILTER_LATITUDE],
        long: params[FILTER_LONGITUDE],
        location: params[FILTER_LOCATION_NAME],
        state: stateSlug,
        city: cityName,
        city_slug: citySlug,
        gender: params[FILTER_GENDER] !== 'any' ? params[FILTER_GENDER] : null,
        price_tier: params[FILTER_PRICE]
      }),
    [params, stateSlug, cityName, citySlug]
  );

  const query = useResourceQuery<SimilarBreedResultsResource>({
    ...similarBreedResultsResourceDefinition(breedSlug, filters),
    enabled: enabled && isSimilarBreedsModuleEnabled && Boolean(breedSlug)
  });

  const meta = query.value.meta || {};
  const data = query.value.data || {};

  return {
    isLoading: query.isLoading,
    filters,
    variant: meta.variant || SIMILAR_BREEDS_VARIANTS.CONTROL,
    resultsType: meta.results_type || SIMILAR_BREEDS_RESULTS_TYPES.PUPPIES,
    radiusInMiles: meta.radius_in_miles,
    totalPuppyCount: meta.total_puppy_count,
    puppies: data.puppies || [],
    breeders: data.breeders || []
  };
}
